import { FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GrupoMaquina, MaquinasPayload } from '@/types/dashboards';
import { dailyDoMes, opsDoMes, resumoOps, seriesDoMes } from '@/utils/equiplexMaquinas';
import { mesVigente } from '@/utils/filtroPaineis';
import type { CoresPainel } from '../chartTheme';
import SeletorMesProducao from '../SeletorMesProducao';
import AvisosMaquinas from './AvisosMaquinas';
import DailyLinhas from './DailyLinhas';
import KpisMaquinas from './KpisMaquinas';
import LinhaDoTempoMaquinas from './LinhaDoTempoMaquinas';
import MfrMatriz from './MfrMatriz';
import OpsMaquinaTable from './OpsMaquinaTable';

interface Props {
  payload: MaquinasPayload;
  cores: CoresPainel;
  /** A meta de uma máquina mudou: buscar a aba de novo (a meta é aplicada na leitura). */
  onMetasSalvas?: () => void;
}

const SeletorGrupo: FC<{ grupos: MaquinasPayload['grupos']; grupo: GrupoMaquina; onGrupo: (g: GrupoMaquina) => void }> = ({
  grupos,
  grupo,
  onGrupo,
}) => (
  <div role="group" aria-label="Linha de produção" className="inline-flex rounded-lg border border-theme-border p-0.5 text-xs">
    {grupos.map((g) => (
      <button
        key={g.id}
        type="button"
        aria-pressed={grupo === g.id}
        onClick={() => onGrupo(g.id)}
        className={`rounded-md px-3 py-1.5 ${grupo === g.id ? 'bg-surface-hover font-medium text-content-primary' : 'text-content-muted hover:text-content-primary'}`}
      >
        {g.rotulo}
      </button>
    ))}
  </div>
);

export type AbaRelatorioDaily = 'daily' | 'mfr';

const ABAS: { id: AbaRelatorioDaily; rotulo: string; icone: string; dica: string }[] = [
  { id: 'daily', rotulo: 'Daily', icone: 'today', dica: 'Produção × programação por linha, com a projeção do mês' },
  { id: 'mfr', rotulo: 'MFR', icone: 'precision_manufacturing', dica: 'Programado × produzido de cada máquina por dia, semana e mês' },
];

/** A aba vem da URL (`?aba=mfr`): o link leva direto ao MFR e sobrevive a um filtro novo. */
function useAba(): [AbaRelatorioDaily, (aba: AbaRelatorioDaily) => void] {
  const [busca, setBusca] = useSearchParams();
  const aba: AbaRelatorioDaily = busca.get('aba') === 'mfr' ? 'mfr' : 'daily';
  const trocar = (nova: AbaRelatorioDaily) =>
    setBusca(
      (atual) => {
        const proxima = new URLSearchParams(atual);
        if (nova === 'daily') proxima.delete('aba');
        else proxima.set('aba', nova);
        return proxima;
      },
      { replace: true },
    );
  return [aba, trocar];
}

const Abas: FC<{ aba: AbaRelatorioDaily; onAba: (aba: AbaRelatorioDaily) => void }> = ({ aba, onAba }) => (
  <div role="tablist" aria-label="Relatório Daily" className="print-hide flex gap-1 border-b border-theme-border">
    {ABAS.map((a) => (
      <button
        key={a.id}
        type="button"
        role="tab"
        id={`maq-aba-${a.id}`}
        aria-selected={aba === a.id}
        aria-controls={`maq-painel-${a.id}`}
        title={a.dica}
        data-testid={`maq-aba-${a.id}`}
        onClick={() => onAba(a.id)}
        className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm transition-colors ${
          aba === a.id
            ? 'border-accent font-semibold text-content-primary'
            : 'border-transparent text-content-muted hover:text-content-primary'
        }`}
      >
        <span aria-hidden className="material-symbols-outlined text-[18px] leading-none">
          {a.icone}
        </span>
        {a.rotulo}
      </button>
    ))}
  </div>
);

/**
 * O Relatório Daily (27/10/2026; antes "Por máquina (MFR)"), em duas abas que dividem o mês:
 * **Daily** — a daily por linha (sopro e envase, com a projeção do mês) — e **MFR** — o que a
 * reunião das 08:00 monta à mão, gerado do Datasul: KPIs, matriz por dia, semana e mês, linha do
 * tempo de cada máquina e as OPs que pedem ação, com o seletor produto acabado × frascos soprados
 * no topo da aba (antes ele ficava no topo da página, longe da matriz). Período e produto vêm do
 * filtro dos painéis (servidor); o mês dentro do período, o grupo e a aba são estado local (a aba
 * na URL). Remonta (via `key`) quando chega snapshot novo.
 */
const PainelMaquinas: FC<Props> = ({ payload, cores, onMetasSalvas }) => {
  const [mesEscolhido, setMes] = useState<string | null>(null);
  const mes = mesVigente(mesEscolhido, payload.meses, payload.mes_padrao);
  const [grupo, setGrupo] = useState<GrupoMaquina>('acabado');
  const mesDesc = payload.meses.find((m) => m.key === mes) ?? payload.meses[payload.meses.length - 1];
  const { maquinas, total } = useMemo(() => seriesDoMes(payload, mes, grupo), [payload, mes, grupo]);
  const ops = useMemo(() => opsDoMes(payload, mes, grupo), [payload, mes, grupo]);
  const resumo = useMemo(() => resumoOps(ops), [ops]);
  const nomes = useMemo(() => [...new Set(ops.map((o) => o.maquina))].sort((a, b) => a.localeCompare(b, 'pt-BR')), [ops]);
  const daily = useMemo(() => dailyDoMes(payload, mes), [payload, mes]);
  const [aba, setAba] = useAba();

  return (
    <>
      {payload.meses.length > 1 && (
        <SeletorMesProducao
          meses={payload.meses}
          mes={mes}
          onMes={setMes}
          extra={(m) => m.em_andamento && <span className="font-normal">· parcial</span>}
        />
      )}
      <Abas aba={aba} onAba={setAba} />
      <AvisosMaquinas payload={payload} mes={mesDesc} />
      {aba === 'daily' ? (
        <div role="tabpanel" id="maq-painel-daily" aria-labelledby="maq-aba-daily" className="flex flex-col gap-6">
          <DailyLinhas
            linhas={daily}
            mes={mesDesc}
            corte={payload.frescor.dados_ate ?? payload.corte}
            cores={cores}
            onMetasSalvas={onMetasSalvas}
          />
          {daily.length === 0 && (
            <p className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm text-content-muted" data-testid="maq-daily-vazio">
              Este snapshot não tem a daily do mês. Ela aparece depois da próxima atualização do painel.
            </p>
          )}
        </div>
      ) : (
        <div role="tabpanel" id="maq-painel-mfr" aria-labelledby="maq-aba-mfr" className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-content-secondary">
              MFR de cada máquina no mês. Escolha a linha de produção: vale para os KPIs, a matriz, a linha do tempo e as
              OPs.
            </p>
            <SeletorGrupo grupos={payload.grupos} grupo={grupo} onGrupo={setGrupo} />
          </div>
          <KpisMaquinas total={total} maquinas={maquinas} resumo={resumo} />
          <MfrMatriz maquinas={maquinas} total={total} mes={mesDesc} limiares={payload.limiares} />
          <LinhaDoTempoMaquinas maquinas={maquinas} mes={mesDesc} limiares={payload.limiares} cores={cores} />
          <OpsMaquinaTable key={`${mes}-${grupo}`} ops={ops} maquinas={nomes} itens={payload.itens} modo={cores.modo} />
        </div>
      )}
      <details className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm" data-testid="maq-notas">
        <summary className="cursor-pointer font-semibold text-content-primary">Como os números são calculados</summary>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-content-secondary">
          {payload.notas.map((n) => (
            <li key={n}>{n}</li>
          ))}
          <li>Fonte: réplica MySQL do Datasul (carga diária às 02:00); painel gerado às 06:00, com os dias até 23:59 do dia anterior.</li>
        </ul>
      </details>
    </>
  );
};

export default PainelMaquinas;
